import { AxiosRequestConfig } from 'axios';
import type { RequestCache } from './type';
import md5 from 'md5';
import cloneDeep from 'lodash/cloneDeep.js';

class CacheManager {
  private cacheMap: Map<string, RequestCache> = new Map();

  /**
   * 根据请求生成key
   * @param config axios请求
   */
  public getCacheKey(config: AxiosRequestConfig): string {
    const { method, url, params, data } = config;
    const arr = [method, url];
    if (params) {
      arr.push(JSON.stringify(params));
    }
    if (data) {
      if (data instanceof FormData) {
        data.forEach((value, key) => {
          arr.push(key + '=' + (typeof value !== 'string' ? Date.now() : value));
        });
      } else {
        arr.push(JSON.stringify(data));
      }
    }
    return md5(arr.join('&'));
  }

  /**
   * 获取缓存
   * @param config axios请求
   */
  public get<T>(config: AxiosRequestConfig): Promise<T> | undefined {
    const key = this.getCacheKey(config);
    const cache = this.cacheMap.get(key);
    if (cache) {
      if (cache.status === 'complete') {
        return Promise.resolve(cloneDeep(cache.res) as T);
      }
      if (cache.status === 'pending') {
        return new Promise((resolve, reject) => {
          cache.callback.push({
            resolve,
            reject,
          });
        });
      }
    }
    this.cacheMap.set(key, {
      status: 'pending',
      callback: [],
    });
  }

  /**
   * 设置缓存
   * @param config axios请求
   * @param res 接口正常返回数据
   * @param error 错误信息
   */
  public set(config: AxiosRequestConfig, res: any, error?: any) {
    const key = this.getCacheKey(config);
    const cache = this.cacheMap.get(key);
    if (cache) {
      if (res) {
        cache.res = cloneDeep(res);
        cache.status = 'complete';
        for (const cb of cache.callback) {
          cb.resolve(cloneDeep(res));
        }
        cache.callback.length = 0;
      } else if (error) {
        for (const cb of cache.callback) {
          cb.reject(error);
        }
        this.cacheMap.delete(key);
      }
    }
  }
}

export default CacheManager;
